import { createFileRoute } from '@tanstack/react-router';
import { CaseStudyLayout, type CaseStudyContent } from '../components/CaseStudyLayout.js';
import { BookingMockup } from '../components/visuals/BookingMockup.js';
import { IconImaging } from '../components/visuals/IndustryIcons.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/for-medical-imaging')({
  component: MedicalImagingPage,
});

function MedicalImagingPage() {
  usePageTitle('Bookings for medical imaging clinics');
  return <CaseStudyLayout content={content} />;
}

const content: CaseStudyContent = {
  eyebrow: 'For medical imaging',
  icon: IconImaging,
  headline:
    'Keep the right room free for the patient who needs it.',
  headlineEmphasis: 'right room',
  subhead:
    'Bookaburra checks room features and staff skills before a patient can book. Fewer clashes. Fewer morning recoveries.',
  Visual: BookingMockup,
  business: {
    name: 'Sunshine Imaging',
    location: 'Sydney · 10 staff · 5 rooms',
    summary:
      'Imagine an Australian radiology clinic with two ultrasound rooms, one CT-only room, one combined MRI/CT room, an X-ray room, and ten staff with overlapping certifications — radiologists, sonographers across general, obstetric and cardiac scopes, and radiographers across CT, MRI and X-ray. Operating hours are 08:00 to 17:00 weekdays and 08:00 to 12:00 Saturday.',
  },
  demoBookingSlug: 'sunshine-imaging',
  dayTimeline: [
    {
      time: '8:00',
      event:
        'The day opens. CT-only room has six contrast studies booked, MRI/CT room is on MRI all morning, both ultrasound rooms are busy with general scans.',
      bookaburraResponse:
        'Nothing to do. Each booking was held against the right room feature when it was made.',
    },
    {
      time: '9:15',
      event:
        'A GP referral comes through for an obstetric ultrasound. The patient picks the next available time online.',
      bookaburraResponse:
        'Only offers slots where an obstetric-credentialed sonographer is rostered. Two of five sonographers qualify; she gets a 10:30 spot.',
    },
    {
      time: '10:20',
      event:
        'A walk-in needs an X-ray. The combined MRI/CT room happens to be empty for 20 minutes.',
      bookaburraResponse:
        'Routes the X-ray to the X-ray room instead. The combined room is held for work that genuinely needs it.',
    },
    {
      time: '11:40',
      event:
        'Priya, the CT-credentialed radiographer, calls in sick for the afternoon. Six CT studies are on her sheet.',
      bookaburraResponse:
        'Reshuffle drafts a plan: four studies move to Tom (also CT-credentialed), two slide to tomorrow morning. Manager reviews and approves before any patient is messaged.',
    },
    {
      time: '15:30',
      event:
        'A specialist opens the picker to add a second MRI for late afternoon. The MRI/CT room is set to CT until 16:30.',
      bookaburraResponse:
        '16:00 isn’t offered — MRI needs the MRI feature, and the room is in CT mode for that window. Earliest MRI slot shown is 16:45.',
    },
  ],
  pains: [
    {
      quote: 'Your CT room gets booked for an X-ray and the actual CT patient has to wait.',
      explanation:
        'The calendar saw a free room. It did not understand which room should stay free for CT and MRI work.',
    },
    {
      quote: 'Two of our sonographers are obstetric-qualified and three are not. Reception books whichever name appears first.',
      explanation:
        'The team knows who can do the work. The booking page should know too.',
    },
    {
      quote: 'Our best radiographer calls in sick and someone is on the phone for two hours rescheduling six appointments.',
      explanation:
        'Every move needs the right room, the right staff member, and a clear message to the patient.',
    },
    {
      quote: 'A patient asks for the report in Mandarin and the team copies and pastes from Google Translate.',
      explanation:
        'Important instructions should be consistent, approved, and stored once.',
    },
  ],
  solutions: [
    {
      title: 'Stop wasting flexible rooms',
      body: (
        <>
          A CT scan needs a room with the <em>CT</em> feature. Bookaburra uses
          the CT-only room before the combined MRI/CT room, so the flexible room
          stays free for work only it can do.
        </>
      ),
    },
    {
      title: 'Stop assigning the wrong staff',
      body: (
        <>
          Services declare the skills they need, such as obstetric sonography or
          CT radiography. The booking page only offers a slot when someone with
          that skill is available.
        </>
      ),
    },
    {
      title: 'Avoid panic when someone is sick',
      body: (
        <>
          Mark a radiographer as sick and Bookaburra drafts the day&rsquo;s changes:
          who moves, who is reassigned, and what needs manual attention. You
          approve it before patients are notified.
        </>
      ),
    },
    {
      title: 'Stop rewriting patient instructions',
      body: (
        <>
          Service names, prep instructions, and confirmations use the
          patient&rsquo;s preferred language. Your team stores the wording once and
          sends it consistently.
        </>
      ),
    },
  ],
  industrySlug: 'imaging',
  mockScheduleSlug: 'sunshine-imaging',
};
